import React, { useState } from 'react';
import {
    Calendar as BigCalendar,
    momentLocalizer,
    Views,
} from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { List, Map } from 'immutable';
import moment from 'moment';
import './css/calendar-week.less';

import { updateEvent } from 'flow/actions/calendar-actions';
import SettingsBlock from 'flow/components/settings/SettingsBlock';
import Button from 'flow/components/ui-components/Button';
import Icon from 'flow/components/ui-components/Icon';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { Calendar } from 'flow/types/Calendar';
import {
    ChannelKey,
    ContactKey,
    ExtensionKey,
    ProfileKey,
    QueueKey,
    ReferKey,
} from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import {
    getBgColorFromKey_TW,
    isExtensionKey,
    ISO_8601_MS_RESOLUTION,
} from 'flow/utils';
import { addGeneralLogEntry } from 'flow/utils/browser-utils';
import {
    getFilteredCalendarEvents,
    inflateOpeningHours,
    isAllDayEvent,
    isFromSocialIntegration,
} from 'flow/utils/calendar-utils';
import { resolveDefaultDuration } from 'flow/utils/profile-utils';
import { twMerge } from 'tailwind-merge';
import ProfileThumbnail from './ProfileThumbnail';

const localizer = momentLocalizer(moment);

// Wrap the calendar in a higher-level component with Drag and Drop
// functionality, but disable the HTML5Backend. The "withDragAndDrop"
// attempts to create its own HTML5Backend if we do not turn this off,
// and 'react-dnd' does not allow for several backends to live inside
// the same tree at the same time. Since we attach a (HTML5)backend to
// the root App in Main, we have to disable this.
const DNDBigCalendar = withDragAndDrop(BigCalendar, { backend: false });

interface Props {
    entityKey: ChannelKey | ContactKey | ExtensionKey | QueueKey | ReferKey;
    calendar: TypedMap<Calendar>;
    profiles: List<TypedMap<Profile>>;
    handleClearCalendar: () => void;
}

interface DNDCalendarEvent {
    start: Date;
    end: Date;
    event: {
        eventKey: string;
    };
    eventKey: string;
    type: string;
}

const CalendarWeek = ({
    entityKey,
    calendar,
    profiles,
    handleClearCalendar,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const { ownExtension } = useExtension();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const isOwnExtension = entityKey === ownExtensionKey;

    const [viewingDate, setViewingDate] = useState(new Date());
    const [profileKeyBeingDragged, setProfileKeyBeingDragged] =
        useState<ProfileKey>('');

    // This partially prevents a bug where you can drop the profile
    // below the calendar, and it will trigger a create event.
    const [allowProfileDrop, setAllowProfileDrop] = useState(false);

    const createEventWithProfile = ({ start }: DNDCalendarEvent) => {
        if (!profileKeyBeingDragged) {
            addGeneralLogEntry(
                'Tried to create calendar event, but missing profile key',
            );
            console.warn('Cannot create event. Missing profile key.');
            return;
        }

        if (!allowProfileDrop) return;
        setAllowProfileDrop(false);

        // Calculate the correct end time by getting the default duration of the profile
        const profile = profiles.find(
            (profile) => profile.get('key') === profileKeyBeingDragged,
        );

        const startMoment = moment(start);
        const defaultDurationTime =
            profile &&
            resolveDefaultDuration(profile.get('defaultDuration'), startMoment);

        openModal('createCalendarEvent', {
            entityKey,
            start: startMoment,
            end: defaultDurationTime || startMoment.add(1, 'hours'),
            profile,
        });
    };

    // This should only be called when dragging an existing event
    const onEventDrop = (calendarEventWithProfile: DNDCalendarEvent) => {
        const { eventKey } = calendarEventWithProfile.event;

        if (!eventKey) return;

        const start = moment(calendarEventWithProfile.start)
            .second(0)
            .millisecond(0)
            .format(ISO_8601_MS_RESOLUTION);

        const end = moment(calendarEventWithProfile.end)
            .second(0)
            .millisecond(0)
            .format(ISO_8601_MS_RESOLUTION);

        const calendarEvent = calendar
            .get('events')
            .find((e) => e.get('key') === decodeURIComponent(eventKey))
            .set('end', end)
            .set('start', start);

        dispatch(updateEvent(calendarEvent, entityKey));
    };

    const getCustomToolbar = (toolbar: any) => (
        <div className="mb-2 flex items-center justify-between">
            <div className="flow-list-item-title">{toolbar.label}</div>
            <div className="flex gap-1">
                <Button
                    className="!min-w-8 rounded-e-none"
                    onClick={() => {
                        toolbar.onNavigate('PREV');
                    }}
                    color="ghost"
                >
                    <Icon
                        iconKey="chevron_left"
                        className="rtl:scale-x-[-1]"
                        size="1rem"
                    />
                </Button>
                <Button
                    className="rounded-none"
                    onClick={() => {
                        toolbar.onNavigate('TODAY');
                    }}
                    color="ghost"
                >
                    {i18next.t('today')}
                </Button>
                <Button
                    className="!min-w-8 rounded-s-none"
                    onClick={() => {
                        toolbar.onNavigate('NEXT');
                    }}
                    color="ghost"
                >
                    <Icon
                        iconKey="chevron_right"
                        className="rtl:scale-x-[-1]"
                        size="1rem"
                    />
                </Button>
            </div>
        </div>
    );

    const toReactBigCalendarEvent = (event: any) =>
        Map({
            start: moment(event.get('start')).toDate(),
            end: moment(event.get('end')).toDate(),
            title: event.getIn(['profile', 'description'], '?'),
            allDay: isAllDayEvent(event),
            profileKey: event.getIn(['profile', 'key'], null),
            eventKey: event.get('key'),
        });

    const renderEvent = ({ event }: any) => {
        //If we have an extensionKey, the "left for the day" profile has plusId 3. If we have something else, such as a queue, it has plusId 1 "closed".
        const getPlusIDReference = isExtensionKey(entityKey) ? 3 : 1;
        const leftForTheDayProfile = profiles?.find(
            (profile) => profile.get('plusId') === getPlusIDReference,
        );
        const isWorkHour = event.type === 'workHour';
        const leftForTheDayLabel = leftForTheDayProfile
            ? leftForTheDayProfile.get('description')
            : i18next.t('pbx.closed');
        const profileKey = isWorkHour
            ? leftForTheDayProfile?.get('key')
            : event.profileKey;

        return (
            <div
                className={twMerge(
                    'custom-event',
                    !isWorkHour && calendar.get('editable')
                        ? ''
                        : 'non-editable',
                )}
            >
                <div
                    className={twMerge(
                        'w-2.5 shrink-0',
                        getBgColorFromKey_TW(profileKey),
                    )}
                />
                <div
                    className={twMerge(
                        'event-content',
                        `${getBgColorFromKey_TW(profileKey)}/50`,
                    )}
                >
                    <div className="custom-event-time">
                        {event.allDay
                            ? null
                            : moment(event.start).format('HH:mm') +
                              ' - ' +
                              moment(event.end).format('HH:mm')}
                    </div>
                    <div className="custom-event-title">
                        {isWorkHour ? leftForTheDayLabel : event.title}
                    </div>
                    <div className="custom-event-dummy" />
                </div>
            </div>
        );
    };

    const components = {
        event: renderEvent,
        toolbar: getCustomToolbar,
    };

    const formats = {
        dateFormat: 'dd',
        dayFormat: (date: any, culture: any, localizer: any) =>
            localizer.format(date, 'ddd D', culture),
        dayRangeHeaderFormat: ({ start, end }: any, culture: any, local: any) =>
            local.format(start, 'MMM D', culture) +
            ' - ' +
            local.format(end, 'MMM D', culture),
        timeGutterFormat: 'HH:mm',
    };

    const startInterval = moment(viewingDate).startOf('week');
    const endInterval = moment(viewingDate).endOf('week');

    const openingHoursEvents = inflateOpeningHours(
        calendar.get('openingHours'),
        startInterval,
        endInterval,
    );

    const recurringEvents = getFilteredCalendarEvents(
        calendar.get('events'),
        startInterval,
        endInterval,
    ).map(toReactBigCalendarEvent);

    const events = List()
        .concat(recurringEvents)
        .concat(openingHoursEvents)
        .toJS();

    return (
        <div className="big-calendar flex flex-col gap-3">
            <SettingsBlock className="flex flex-col gap-3">
                <DNDBigCalendar
                    selectable={
                        calendar.get('editable') === false
                            ? calendar.get('editable')
                            : 'ignoreEvents'
                    }
                    onSelectSlot={(newCalendarEvent: DNDCalendarEvent) =>
                        openModal('createCalendarEvent', {
                            entityKey,
                            start: moment(newCalendarEvent.start),
                            end: moment(newCalendarEvent.end),
                        })
                    }
                    onSelectEvent={(event: DNDCalendarEvent) => {
                        if (event.eventKey && calendar.get('editable')) {
                            openModal('editCalendarEvent', {
                                entityKey,
                                calendarEvent: calendar
                                    .get('events')
                                    .find(
                                        (e) => e.get('key') === event.eventKey,
                                    ),
                            });
                        }
                    }}
                    timeslots={4}
                    step={30}
                    formats={formats}
                    events={events}
                    defaultView={Views.WEEK}
                    views={[Views.WEEK]}
                    toolbar={true}
                    components={components}
                    onNavigate={setViewingDate}
                    onEventDrop={onEventDrop}
                    onDropFromOutside={createEventWithProfile}
                    localizer={localizer}
                    onDragOver={() => {
                        if (!allowProfileDrop) setAllowProfileDrop(true);
                    }}
                    draggableAccessor={(event: DNDCalendarEvent) =>
                        event.type !== 'workHour'
                    }
                />

                {profiles && !profiles.isEmpty() && (
                    <div className="flex flex-wrap gap-2">
                        {profiles.map((profile) => (
                            <ProfileThumbnail
                                key={`profile-${profile.get('key')}`}
                                draggable={calendar.get('editable')}
                                setProfileKeyBeingDragged={
                                    setProfileKeyBeingDragged
                                }
                                profile={profile}
                            />
                        ))}
                    </div>
                )}
            </SettingsBlock>

            {(isOwnExtension || ownExtension.get('admin')) &&
                handleClearCalendar && (
                    <Button
                        color="danger"
                        onClick={() =>
                            openModal('removeDialog', {
                                title: i18next.t(
                                    'ClearCalendarDialog.Extension.Title',
                                ),
                                description: i18next.t(
                                    'ClearCalendarDialog.Extension.Description',
                                ),
                                onRemove: handleClearCalendar,
                            })
                        }
                        disabled={isFromSocialIntegration(calendar)}
                    >
                        {i18next.t('calendar.clear')}
                    </Button>
                )}
        </div>
    );
};

export default CalendarWeek;
